import React, { useState } from "react";
import axios from "axios";
import {
  Search, Building2, Users, DollarSign, Globe, MapPin, Calendar, Linkedin,
  CheckCircle2, Plus, Sparkles, Loader2, X, ExternalLink, AlertTriangle
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── Result Card ──────────────────────────────────────────────────────────
function ResultCard({ org, onSave, savingId, savedIds }) {
  const isSaved = savedIds.has(org.id);
  const isSaving = savingId === org.id;
  return (
    <div data-testid="apollo-result-card" className="group border border-white/[0.04] bg-[#0D0F12] hover:border-[#00E5FF]/40 transition-all duration-300 overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-[#00E5FF] to-transparent" />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          {org.logo_url ? (
            <img src={org.logo_url} alt={org.name} className="w-12 h-12 object-contain border border-white/[0.04] bg-white p-1 flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 flex items-center justify-center border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex-shrink-0">
              <Building2 className="w-5 h-5 text-[#00E5FF]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white leading-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }} title={org.name}>{org.name}</h3>
            {org.domain && (
              <a href={`https://${org.domain}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-[#00E5FF] hover:text-white transition-colors mt-0.5">
                <Globe className="w-3 h-3" /> {org.domain}
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
            )}
          </div>
          <span className="inline-flex items-center gap-1 border border-[#A78BFA]/40 bg-[#A78BFA]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#A78BFA] tracking-widest uppercase">
            <Sparkles className="w-2.5 h-2.5" /> Apollo
          </span>
        </div>

        {org.industry && (
          <div className="mb-3">
            <span className="inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF] font-semibold">
              {org.industry}
            </span>
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          <div className="border border-white/[0.04] bg-[#050505] p-2 text-center">
            <p className="text-[8px] uppercase tracking-widest text-[#A0AEC0]">Employees</p>
            <p className="text-sm font-bold tabular-nums text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              {org.estimated_num_employees ? org.estimated_num_employees.toLocaleString("it-IT") : "—"}
            </p>
          </div>
          <div className="border border-white/[0.04] bg-[#050505] p-2 text-center">
            <p className="text-[8px] uppercase tracking-widest text-[#A0AEC0]">Revenue</p>
            <p className="text-sm font-bold tabular-nums text-[#16C784] truncate" style={{ fontFamily: "Chivo, sans-serif" }} title={org.annual_revenue_printed}>
              {org.annual_revenue_printed || "—"}
            </p>
          </div>
          <div className="border border-white/[0.04] bg-[#050505] p-2 text-center">
            <p className="text-[8px] uppercase tracking-widest text-[#A0AEC0]">Fondata</p>
            <p className="text-sm font-bold tabular-nums text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              {org.founded_year || "—"}
            </p>
          </div>
        </div>

        {(org.hq_city || org.hq_country) && (
          <div className="flex items-center gap-1.5 text-xs text-[#CBD5E1] mb-3">
            <MapPin className="w-3 h-3 text-[#A0AEC0] flex-shrink-0" />
            <span>{[org.hq_city, org.hq_country].filter(Boolean).join(", ")}</span>
          </div>
        )}

        {org.keywords && org.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {org.keywords.slice(0, 6).map((k) => (
              <span key={k} className="text-[9px] px-1.5 py-0.5 bg-[#0A0C10] border border-white/[0.04] text-[#CBD5E1] tracking-wide">{k}</span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-white/[0.04]">
          {org.linkedin_url && (
            <a href={org.linkedin_url} target="_blank" rel="noopener noreferrer"
               className="flex-shrink-0 flex items-center gap-1 px-2 py-2 border border-white/[0.04] bg-[#050505] hover:border-[#A78BFA]/40 hover:text-[#A78BFA] text-[#CBD5E1] transition-colors">
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            data-testid={`apollo-save-${org.id}`}
            onClick={() => onSave(org)}
            disabled={isSaved || isSaving}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-[11px] font-bold tracking-widest uppercase transition-all ${
              isSaved
                ? "bg-[#16C784]/15 border border-[#16C784]/50 text-[#16C784]"
                : "bg-[#00E5FF] text-black hover:bg-white disabled:opacity-60"
            }`}
          >
            {isSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Salvo…</>
             : isSaved ? <><CheckCircle2 className="w-3.5 h-3.5" /> Salvato</>
             : <><Plus className="w-3.5 h-3.5" /> Aggiungi ai profili</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
export default function AccountCheck() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [toast, setToast] = useState(null);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (q.length < 2) { setError("Inserisci almeno 2 caratteri"); return; }
    setLoading(true); setError(""); setResults([]); setTotal(0);
    try {
      const { data } = await axios.post(`${API}/apollo/search`, { query: q }, { withCredentials: true });
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante la ricerca");
    } finally { setLoading(false); }
  };

  const handleSave = async (org) => {
    setSavingId(org.id);
    try {
      const { data } = await axios.post(`${API}/apollo/save`, { data: org }, { withCredentials: true });
      setSavedIds(prev => new Set([...prev, org.id]));
      setToast({
        type: "success",
        title: data.already_existed ? "Aggiornato" : "Salvato",
        msg: `${org.name} è stato ${data.already_existed ? "aggiornato" : "aggiunto"} ai profili aziende. Ora è visibile in Sales · Business Watch.`,
      });
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      setToast({ type: "error", title: "Errore", msg: err.response?.data?.detail || "Impossibile salvare il profilo" });
      setTimeout(() => setToast(null), 5000);
    } finally { setSavingId(null); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      {/* Hero header */}
      <div>
        <div className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#A78BFA] font-bold">
          <Sparkles className="w-3 h-3" /> KYC Monitor · Account Check · Apollo.io
        </div>
        <h1 className="text-4xl font-bold text-white tracking-tight leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Account Check</h1>
        <p className="text-sm text-[#A0AEC0] mt-1">Cerca aziende via Apollo.io in tempo reale e aggiungile ai tuoi profili con un click.</p>
      </div>

      {/* Search bar */}
      <form onSubmit={doSearch} className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
          <input
            data-testid="apollo-search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca un&apos;azienda per nome… (es. Stripe, OmniSteel, Danieli)"
            className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#6B7280]"
            autoFocus
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="submit"
          data-testid="apollo-search-button"
          disabled={loading}
          className="bg-[#00E5FF] text-black px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cerco…</> : <><Search className="w-3.5 h-3.5" /> Cerca in Apollo</>}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div data-testid="apollo-error" className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-[#FF3B30] flex-shrink-0" />
          <span className="text-[#FF3B30]">{error}</span>
        </div>
      )}

      {/* Results header */}
      {results.length > 0 && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#6B7280] pt-2">
          <span>
            <span className="text-white font-bold tabular-nums">{results.length}</span>
            {total > results.length && <span> di <span className="text-white font-bold tabular-nums">{total.toLocaleString("it-IT")}</span></span>} risultati
          </span>
          <span className="flex items-center gap-1.5 text-[#A78BFA]">
            <Sparkles className="w-2.5 h-2.5" /> Powered by Apollo.io
          </span>
        </div>
      )}

      {/* Empty state (no search yet) */}
      {!loading && !error && results.length === 0 && (
        <div className="border border-dashed border-white/[0.04] bg-[#0D0F12] py-16 flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 border border-[#A78BFA]/40 bg-[#A78BFA]/10 flex items-center justify-center mb-4">
            <Sparkles className="w-7 h-7 text-[#A78BFA]" />
          </div>
          <p className="text-white font-semibold mb-1">Cerca un&apos;azienda per iniziare</p>
          <p className="text-xs text-[#A0AEC0] max-w-md mb-4">
            Inserisci il nome di un&apos;azienda nella barra sopra. Apollo restituirà fino a 10 corrispondenze arricchite con dati aziendali, contatti chiave e KPI.
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="border border-white/[0.04] bg-[#0D0F12] p-5">
              <div className="flex gap-3 mb-4">
                <div className="w-12 h-12 bg-[#1F242D] animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#1F242D] animate-pulse w-3/4" />
                  <div className="h-2 bg-[#1F242D] animate-pulse w-1/2" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <div className="h-10 bg-[#1F242D] animate-pulse" />
                <div className="h-10 bg-[#1F242D] animate-pulse" />
                <div className="h-10 bg-[#1F242D] animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results grid */}
      {!loading && results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {results.map((org, i) => (
            <ResultCard key={org.id || i} org={org} onSave={handleSave} savingId={savingId} savedIds={savedIds} />
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="apollo-toast"
             className="fixed bottom-6 right-6 max-w-sm border p-4 shadow-2xl z-50"
             style={{
               borderColor: toast.type === "success" ? "#059669" : "#DC2626",
               background: toast.type === "success" ? "#0D0F12" : "#1A0B0B",
               boxShadow: `0 20px 60px -20px ${toast.type === "success" ? "#16C78455" : "#FF3B3055"}`,
             }}>
          <div className="flex items-start gap-3">
            {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 text-[#16C784] flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />}
            <div className="flex-1">
              <p className="text-sm font-bold text-white mb-0.5">{toast.title}</p>
              <p className="text-xs text-[#CBD5E1] leading-relaxed">{toast.msg}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-[#A0AEC0] hover:text-white flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
